import { useState } from 'react'
import { Plus, X, Copy, Pencil, Check, Cloud, CloudCheck, CloudAlert, ListChecks } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useHomeStore } from '../store'
import { createSearch, updateSearch } from '../searchesApi'

export function SessionsTabBar() {
  const sessionOrder = useHomeStore((s) => s.sessionOrder)
  const sessions = useHomeStore((s) => s.sessions)
  const currentSessionId = useHomeStore((s) => s.currentSessionId)
  const switchSession = useHomeStore((s) => s.switchSession)
  const createSession = useHomeStore((s) => s.createSession)
  const renameSession = useHomeStore((s) => s.renameSession)
  const deleteSession = useHomeStore((s) => s.deleteSession)
  const duplicateSession = useHomeStore((s) => s.duplicateSession)
  const setSavedSearchId = useHomeStore((s) => s.setSavedSearchId)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftName, setDraftName] = useState('')
  const [saveState, setSaveState] = useState<Record<string, 'idle' | 'saving' | 'saved' | 'error'>>({})

  async function saveCurrent(id: string) {
    const session = sessions[id]
    if (!session) return
    setSaveState((s) => ({ ...s, [id]: 'saving' }))
    try {
      const payload = {
        general: session.general,
        vehicles: session.vehicles,
        nextVehicleId: session.nextVehicleId,
      }
      if (session.savedSearchId) {
        await updateSearch(session.savedSearchId, { name: session.name, data: payload })
      } else {
        const created = await createSearch(session.name, payload)
        setSavedSearchId(id, created.id)
      }
      setSaveState((s) => ({ ...s, [id]: 'saved' }))
      setTimeout(() => setSaveState((s) => ({ ...s, [id]: 'idle' })), 2500)
    } catch {
      setSaveState((s) => ({ ...s, [id]: 'error' }))
      setTimeout(() => setSaveState((s) => ({ ...s, [id]: 'idle' })), 3500)
    }
  }

  function startEdit(id: string, name: string) {
    setEditingId(id)
    setDraftName(name)
  }

  function commit(id: string) {
    const name = draftName.trim()
    if (name) renameSession(id, name)
    setEditingId(null)
  }

  function onDelete(id: string) {
    const session = sessions[id]
    if (!session) return
    if (session.vehicles.length > 0 || session.general.pojistnik?.COMPANY_NAME) {
      if (!confirm(`Smazat záložku "${session.name}"? Tato akce je nevratná.`)) return
    }
    deleteSession(id)
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-zinc-200 bg-white p-2 shadow-sm">
      {sessionOrder.map((id) => {
        const s = sessions[id]
        if (!s) return null
        const active = id === currentSessionId
        const editing = editingId === id
        return (
          <div
            key={id}
            className={cn(
              'group flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition',
              active
                ? 'border-brand bg-brand-bg text-brand'
                : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50',
            )}
          >
            {editing ? (
              <>
                <Input
                  autoFocus
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commit(id)
                    if (e.key === 'Escape') setEditingId(null)
                  }}
                  className="h-7 w-44 px-2 py-0 text-xs"
                />
                <button
                  type="button"
                  onClick={() => commit(id)}
                  className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
                  title="Potvrdit"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={() => switchSession(id)} className="font-medium">
                  {s.name}
                </button>
                <span className="text-[10px] opacity-60">{s.vehicles.length} voz.</span>
                <button
                  type="button"
                  onClick={() => saveCurrent(id)}
                  className="rounded p-1 opacity-0 group-hover:opacity-100 hover:bg-black/5"
                  title="Uložit do účtu"
                  disabled={saveState[id] === 'saving'}
                >
                  {saveState[id] === 'saving' ? (
                    <Cloud className="h-3 w-3 animate-pulse" />
                  ) : saveState[id] === 'saved' ? (
                    <CloudCheck className="h-3 w-3 text-emerald-600" />
                  ) : saveState[id] === 'error' ? (
                    <CloudAlert className="h-3 w-3 text-red-600" />
                  ) : (
                    <Cloud className="h-3 w-3" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => startEdit(id, s.name)}
                  className="rounded p-1 opacity-0 group-hover:opacity-100 hover:bg-black/5"
                  title="Přejmenovat"
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => duplicateSession(id)}
                  className="rounded p-1 opacity-0 group-hover:opacity-100 hover:bg-black/5"
                  title="Duplikovat"
                >
                  <Copy className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(id)}
                  className="rounded p-1 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600"
                  title="Smazat z prohlížeče"
                >
                  <X className="h-3 w-3" />
                </button>
              </>
            )}
          </div>
        )
      })}

      <Button variant="ghost" size="sm" onClick={() => createSession()} className="text-xs">
        <Plus className="mr-1 h-3.5 w-3.5" />
        Nové vyhledávání
      </Button>
      <Link
        to="/history"
        className="inline-flex items-center gap-1 rounded-md px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-100"
      >
        <ListChecks className="h-3.5 w-3.5" />
        Uložená vyhledávání
      </Link>
    </div>
  )
}
